import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig, { getSchemaId } from '../../config/gobodoConfig';
import type { ObjectRelationship, CreateRelationshipInput, UpdateRelationshipInput } from '../queries/objectRelationshipQueries';
import { resolveObjectKey, keyToObjectId, getObjectTypeByKeyWithFallback } from './gobodoObjectService';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import type { CustomObject } from '../queries/objectQueries';

// ============================================
// TYPE CONVERSION
// ============================================

/**
 * Converts CreateRelationshipInput to Gobodo GraphRelationshipTypeInput
 */
function convertToGobodoRelationshipType(
  input: CreateRelationshipInput,
  schemaId: number
): GobodoApi.GraphRelationshipTypeInput {
  const relationshipInput = new GobodoApi.GraphRelationshipTypeInput([], [], {});
  
  // Set basic properties
  relationshipInput.name = input.fieldName; // Use field name as relationship name
  relationshipInput.key = input.fieldName.toLowerCase(); // Use lowercase field name as key
  relationshipInput.description = input.description || '';
  relationshipInput.descriptiveName = input.fieldLabel; // Field label as descriptive name
  relationshipInput.reverseDescriptiveName = input.childRelationshipName; // Child relationship name
  relationshipInput.createName = input.fieldLabel;
  relationshipInput.reverseName = input.childRelationshipName;
  relationshipInput.pluralName = input.childRelationshipName; // Usually plural
  relationshipInput.reversePluralName = input.sourceObjectLabel + 's'; // Source object plural
  
  // Set cardinality based on relationship type
  // Lookup = MANY_TO_ONE (many source records can reference one target)
  // Master-Detail = MANY_TO_ONE with parent-child properties
  relationshipInput.cardinality = GobodoApi.GraphRelationshipTypeInput.CardinalityEnum.MANY_TO_ONE;

  relationshipInput.schemaid = schemaId;
  relationshipInput.startObjectTypes = []; // Will be populated
  relationshipInput.endObjectTypes = []; // Will be populated

  relationshipInput.properties = {
    relationshipType: input.relationshipType,
    isMasterDetail: input.relationshipType === 'master-detail',
    cascadeDelete: input.cascadeDelete || false,
    shareAccessLevel: input.shareAccessLevel || 'None',
    isRequired: input.isRequired || false,
    lookupFilter: input.lookupFilter || undefined
  };
  
  return relationshipInput;
}

/**
 * Converts Gobodo GraphRelationshipTypeResponse to ObjectRelationship
 */
function convertFromGobodoRelationshipType(
  gobodoRelType: any,
  sourceObjectId: string,
  sourceObjectName: string,
  sourceObjectLabel: string
): ObjectRelationship {
  // Extract properties
  const properties = gobodoRelType.properties || {};
  // Prefer API's ismasterdetail flag when present for read-back consistency
  const relationshipType = gobodoRelType.ismasterdetail === true ? 'master-detail' : (properties.relationshipType || 'lookup');
  
  // Get start and end object types
  const startObjectType = gobodoRelType.startObjectTypes?.[0];
  const endObjectType = gobodoRelType.endObjectTypes?.[0];
  
  // Convert end object type to our format
  const targetObjectKey = endObjectType?.key || endObjectType?.name || '';
  const targetObjectId = targetObjectKey ? keyToObjectId(targetObjectKey) : '';
  
  return {
    id: `rel_${gobodoRelType.id}`,
    sourceObjectId: sourceObjectId,
    sourceObjectName: sourceObjectName,
    sourceObjectLabel: sourceObjectLabel,
    fieldName: gobodoRelType.name || gobodoRelType.key,
    fieldLabel: gobodoRelType.descriptiveName || gobodoRelType.name,
    targetObjectId: targetObjectId,
    targetObjectName: targetObjectKey,
    targetObjectLabel: endObjectType?.singularname || endObjectType?.name || '',
    relationshipType: relationshipType as 'lookup' | 'master-detail',
    cascadeDelete: properties.cascadeDelete || false,
    shareAccessLevel: properties.shareAccessLevel || 'None',
    childRelationshipName: gobodoRelType.reverseDescriptiveName || gobodoRelType.reverseName || '',
    description: gobodoRelType.description,
    isRequired: properties.isRequired || false,
    isStandard: false,
    isActive: true,
    lookupFilter: properties.lookupFilter,
    createdBy: 'System',
    createdDate: gobodoRelType.modificationdate?.toISOString() || new Date().toISOString(),
    lastModified: gobodoRelType.modificationdate?.toISOString() || new Date().toISOString()
  };
}

// ============================================
// RELATIONSHIP TYPE OPERATIONS
// ============================================

/**
 * Create a new relationship type in Gobodo and add it to schema
 * Uses the source object's schema ID based on its category
 */
export async function createGobodoRelationshipType(
  input: CreateRelationshipInput,
  sourceObjectCategory?: 'core' | 'system' | 'custom'
): Promise<ObjectRelationship> {
  const client = getGobodoClient();
  const relationshipTypesApi = new GobodoApi.GraphRelationshipTypesApi(client);
  const schemasApi = new GobodoApi.GraphSchemasApi(client);

  const sourceKey = resolveObjectKey(input.sourceObjectId, input.sourceObjectName);
  const targetKey = resolveObjectKey(input.targetObjectId, input.targetObjectName);

  const sourceObjectType = await getObjectTypeByKeyWithFallback(sourceKey);
  const targetObjectType = await getObjectTypeByKeyWithFallback(targetKey);
  
  // Determine schema ID based on source object's category
  // Default to 'custom' if category is not provided
  const category = sourceObjectCategory || 'custom';
  const schemaId = getSchemaId(category);
  
  // Create relationship type input with the appropriate schema ID
  const relationshipInput = convertToGobodoRelationshipType(input, schemaId);
  
  const startObjectTypeInput = new GobodoApi.GraphObjectTypeInput([], [], {});
  startObjectTypeInput.id = sourceObjectType.id;
  startObjectTypeInput.key = sourceObjectType.key;
  startObjectTypeInput.name = sourceObjectType.name;

  const endObjectTypeInput = new GobodoApi.GraphObjectTypeInput([], [], {});
  endObjectTypeInput.id = targetObjectType.id;
  endObjectTypeInput.key = targetObjectType.key;
  endObjectTypeInput.name = targetObjectType.name;
  
  relationshipInput.startObjectTypes = [startObjectTypeInput];
  relationshipInput.endObjectTypes = [endObjectTypeInput];
  
  // Create relationship type
  const response = await retryWithBackoff(() => relationshipTypesApi.createRelationshipType(relationshipInput), { maxRetries: 3 });
  
  // Add relationship type to schema using the same schema ID
  await retryWithBackoff(() => schemasApi.addSchemaRelationshipTypes(schemaId, [response.id]), { maxRetries: 3 });
  
  // Convert and return
  return convertFromGobodoRelationshipType(
    response,
    input.sourceObjectId,
    input.sourceObjectName,
    input.sourceObjectLabel
  );
}

/**
 * Get relationship type by key
 */
export async function getGobodoRelationshipTypeByKey(key: string): Promise<ObjectRelationship> {
  const client = getGobodoClient();
  const api = new GobodoApi.GraphRelationshipTypesApi(client);
  
  const response = await retryWithBackoff(() => api.getRelationshipTypeByKey(key), { maxRetries: 3 });
  
  // Extract source object info from startObjectTypes
  const startObjectType = response.startObjectTypes?.[0];
  const sourceObjectKey = startObjectType?.key || startObjectType?.name || '';
  const sourceObjectId = sourceObjectKey ? keyToObjectId(sourceObjectKey) : '';
  const sourceObjectName = sourceObjectKey;
  const sourceObjectLabel = startObjectType?.singularname || startObjectType?.name || '';
  
  return convertFromGobodoRelationshipType(
    response,
    sourceObjectId,
    sourceObjectName,
    sourceObjectLabel
  );
}

function parseRelationshipTypeId(relationshipId: string): number {
  const prefix = 'rel_';
  if (relationshipId.startsWith(prefix)) {
    const num = parseInt(relationshipId.slice(prefix.length), 10);
    if (!isNaN(num)) return num;
  }
  const num = parseInt(relationshipId, 10);
  if (!isNaN(num)) return num;
  throw new Error(`Invalid relationship id: ${relationshipId}`);
}

/**
 * Delete a relationship type in Gobodo.
 * Removes it from the custom schema first (if schema list is available) so delete can succeed.
 */
export async function deleteGobodoRelationshipType(relationshipId: string): Promise<void> {
  const client = getGobodoClient();
  const relTypesApi = new GobodoApi.GraphRelationshipTypesApi(client);
  const schemasApi = new GobodoApi.GraphSchemasApi(client);
  const id = parseRelationshipTypeId(relationshipId);
  const customSchemaId = GobodoConfig.schemaCustom();
  try {
    const current = await retryWithBackoff(() => schemasApi.getSchemaRelationshipTypes(customSchemaId, { size: 200 }), { maxRetries: 3 });
    const ids = Array.isArray(current) ? current.map((r: any) => (typeof r === 'object' && r != null ? r.id : r)) : [];
    const filtered = ids.filter((x: number) => Number(x) !== id);
    if (filtered.length < ids.length) {
      await retryWithBackoff(() => schemasApi.addSchemaRelationshipTypes(customSchemaId, filtered), { maxRetries: 3 });
    }
  } catch (_) {
    // Schema list may 500; try delete anyway
  }
  try {
    await retryWithBackoff(() => relTypesApi.deleteRelationshipType(id), { maxRetries: 3 });
  } catch (e: any) {
    const status = e?.response?.status ?? e?.status;
    const statusText = (e?.response?.statusText ?? e?.statusText ?? '').toLowerCase();
    const isForbidden = status === 403 || statusText.includes('forbidden') || String(e?.status).includes('403');
    if (isForbidden) {
      throw new Error('You don’t have permission to delete this relationship. Contact your administrator if you need access.');
    }
    const body = e?.response?.body ?? e?.body;
    const msg =
      (typeof body === 'object' && body != null && body.message) ||
      e?.message ||
      e?.error ||
      (typeof body === 'string' ? body : 'Delete failed');
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }
}

/**
 * Update a relationship type in Gobodo (GET current, merge updates, PUT)
 */
export async function updateGobodoRelationshipType(input: UpdateRelationshipInput): Promise<ObjectRelationship> {
  const client = getGobodoClient();
  const api = new GobodoApi.GraphRelationshipTypesApi(client);
  const id = parseRelationshipTypeId(input.id);

  const current = await retryWithBackoff(() => api.getRelationshipTypeById(id), { maxRetries: 3 });
  const curProps = current.properties || {};
  const mergedProps: Record<string, unknown> = {
    relationshipType: curProps.relationshipType ?? 'lookup',
    cascadeDelete: input.cascadeDelete !== undefined ? input.cascadeDelete : (curProps.cascadeDelete ?? false),
    shareAccessLevel: input.shareAccessLevel !== undefined ? input.shareAccessLevel : (curProps.shareAccessLevel ?? 'None'),
    isRequired: input.isRequired !== undefined ? input.isRequired : (curProps.isRequired ?? false),
    lookupFilter: input.lookupFilter !== undefined ? input.lookupFilter : (curProps.lookupFilter ?? '')
  };

  const relationshipInput = new GobodoApi.GraphRelationshipTypeInput(
    current.fields || [],
    current.methods || [],
    mergedProps
  );
  relationshipInput.name = current.name;
  relationshipInput.key = current.key;
  relationshipInput.description = input.description !== undefined ? input.description : (current.description ?? '');
  const newFieldLabel = input.fieldLabel !== undefined ? input.fieldLabel : (current.descriptiveName ?? current.createName ?? '');
  relationshipInput.descriptiveName = newFieldLabel;
  relationshipInput.createName = newFieldLabel;
  relationshipInput.reverseDescriptiveName = current.reverseDescriptiveName ?? current.reverseName ?? '';
  relationshipInput.reverseName = current.reverseName ?? '';
  relationshipInput.pluralName = current.pluralName ?? '';
  relationshipInput.reversePluralName = current.reversePluralName ?? '';
  relationshipInput.cardinality = current.cardinality;
  relationshipInput.schemaid = current.schemaid ?? getSchemaId('custom');
  relationshipInput.startObjectTypes = (current.startObjectTypes || []).map((t: any) => {
    const o = new GobodoApi.GraphObjectTypeInput([], [], {});
    o.id = t.id;
    o.key = t.key;
    o.name = t.name;
    return o;
  });
  relationshipInput.endObjectTypes = (current.endObjectTypes || []).map((t: any) => {
    const o = new GobodoApi.GraphObjectTypeInput([], [], {});
    o.id = t.id;
    o.key = t.key;
    o.name = t.name;
    return o;
  });
  relationshipInput.properties = mergedProps;

  const response = await retryWithBackoff(() => api.updateRelationshipType(id, relationshipInput), { maxRetries: 3 });
  const startObjectType = response.startObjectTypes?.[0];
  const sourceObjectKey = startObjectType?.key || startObjectType?.name || '';
  const sourceObjectId = sourceObjectKey ? keyToObjectId(sourceObjectKey) : '';
  const sourceObjectName = sourceObjectKey;
  const sourceObjectLabel = startObjectType?.singularname || startObjectType?.name || '';
  return convertFromGobodoRelationshipType(response, sourceObjectId, sourceObjectName, sourceObjectLabel);
}

/**
 * Get all relationship types from all schemas (core, system, custom)
 */
export async function getGobodoRelationshipTypes(): Promise<ObjectRelationship[]> {
  const client = getGobodoClient();
  const schemasApi = new GobodoApi.GraphSchemasApi(client);
  const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
  
  // Get schema IDs for all categories
  const coreSchemaId = GobodoConfig.schemaCore();
  const systemSchemaId = GobodoConfig.schemaSystem();
  const customSchemaId = GobodoConfig.schemaCustom();
  
  // Fetch relationships sequentially to avoid Neo4j transaction collisions
  const coreRelationships = await retryWithBackoff(() => schemasApi.getSchemaRelationshipTypes(coreSchemaId), { maxRetries: 3 }).catch(() => [] as any[]);
  const systemRelationships = await retryWithBackoff(() => schemasApi.getSchemaRelationshipTypes(systemSchemaId), { maxRetries: 3 }).catch(() => [] as any[]);
  const customRelationships = await retryWithBackoff(() => schemasApi.getSchemaRelationshipTypes(customSchemaId), { maxRetries: 3 }).catch(() => [] as any[]);
  
  // Combine all relationships
  const allRelationships: ObjectRelationship[] = [];
  
  // Helper to get object category from object type
  const getObjectCategory = async (objectKey: string): Promise<'core' | 'system' | 'custom'> => {
    try {
      // Try to get object type to determine category
      // For now, we'll need to check all schemas or use a default
      // This is a simplified approach - in production you might cache object categories
      return 'custom'; // Default fallback
    } catch {
      return 'custom';
    }
  };
  
  // Process core relationships
  if (coreRelationships && coreRelationships.length > 0) {
    for (const rel of coreRelationships) {
      const startObjectKey = rel.startObjectTypes?.[0]?.key || rel.startObjectTypes?.[0]?.name || '';
      if (startObjectKey) {
        const sourceObjectId = keyToObjectId(startObjectKey);
        const converted = convertFromGobodoRelationshipType(rel, sourceObjectId, startObjectKey, rel.startObjectTypes?.[0]?.singularname || '');
        allRelationships.push(converted);
      }
    }
  }
  
  // Process system relationships
  if (systemRelationships && systemRelationships.length > 0) {
    for (const rel of systemRelationships) {
      const startObjectKey = rel.startObjectTypes?.[0]?.key || rel.startObjectTypes?.[0]?.name || '';
      if (startObjectKey) {
        const sourceObjectId = keyToObjectId(startObjectKey);
        const converted = convertFromGobodoRelationshipType(rel, sourceObjectId, startObjectKey, rel.startObjectTypes?.[0]?.singularname || '');
        allRelationships.push(converted);
      }
    }
  }
  
  // Process custom relationships
  if (customRelationships && customRelationships.length > 0) {
    for (const rel of customRelationships) {
      const startObjectKey = rel.startObjectTypes?.[0]?.key || rel.startObjectTypes?.[0]?.name || '';
      if (startObjectKey) {
        const sourceObjectId = keyToObjectId(startObjectKey);
        const converted = convertFromGobodoRelationshipType(rel, sourceObjectId, startObjectKey, rel.startObjectTypes?.[0]?.singularname || '');
        allRelationships.push(converted);
      }
    }
  }
  
  return allRelationships;
}

/**
 * Get outgoing relationship types for an object type (where this object is the source/start)
 */
export async function getGobodoObjectTypeOutgoingRelationships(
  objectKey: string,
  objectId?: string,
  objectName?: string,
  objectLabel?: string
): Promise<ObjectRelationship[]> {
  const client = getGobodoClient();
  const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);

  let objectType: { id: number; key: string; name: string; singularname?: string };
  try {
    objectType = await getObjectTypeByKeyWithFallback(objectKey);
  } catch {
    return [];
  }

  let relationshipTypes: any[];
  try {
    relationshipTypes = await retryWithBackoff(() => objectTypesApi.getObjectTypeOutgoingRelationshipTypes(objectType.id), { maxRetries: 3 });
  } catch {
    return [];
  }

  if (!relationshipTypes || relationshipTypes.length === 0) {
    return [];
  }

  const sourceObjectKey = objectName || objectType.key || objectType.name || objectKey;
  const sourceObjectId = objectId || keyToObjectId(sourceObjectKey);
  const sourceObjectLabel = objectLabel || objectType.singularname || objectType.name || '';

  return relationshipTypes.map((relType: any) => {
    return convertFromGobodoRelationshipType(
      relType,
      sourceObjectId,
      sourceObjectKey,
      sourceObjectLabel
    );
  });
}

/**
 * Get incoming relationship types for an object type (where this object is the target/end)
 */
export async function getGobodoObjectTypeIncomingRelationships(
  objectKey: string,
  objectId?: string,
  objectName?: string,
  objectLabel?: string
): Promise<ObjectRelationship[]> {
  const client = getGobodoClient();
  const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);

  let objectType: { id: number; key: string; name: string; singularname?: string };
  try {
    objectType = await getObjectTypeByKeyWithFallback(objectKey);
  } catch {
    return [];
  }

  let relationshipTypes: any[];
  try {
    relationshipTypes = await retryWithBackoff(() => objectTypesApi.getObjectTypeIncomingRelationshipTypes(objectType.id), { maxRetries: 3 });
  } catch {
    return [];
  }

  if (!relationshipTypes || relationshipTypes.length === 0) {
    return [];
  }

  return relationshipTypes.map((relType: any) => {
    const startObjectType = relType.startObjectTypes?.[0];
    const sourceObjectKey = startObjectType?.key || startObjectType?.name || '';
    const sourceObjectId = sourceObjectKey ? keyToObjectId(sourceObjectKey) : '';
    const sourceObjectName = sourceObjectKey;
    const sourceObjectLabel = startObjectType?.singularname || startObjectType?.name || '';

    return convertFromGobodoRelationshipType(
      relType,
      sourceObjectId,
      sourceObjectName,
      sourceObjectLabel
    );
  });
}

